// packages/agent-kit/src/hooks/sprints/useLatestSprintUpdate.ts

import { useMemo } from 'react';
import { useSprintsList } from './useSprints';
import { useSprintBoard } from './useSprintBoard';
import { useTranslation } from 'react-i18next';
import type { LatestUpdateData, TaskLink } from '@dopomogai/types';

/**
 * A composite hook that finds the single most recent "update" (defined as a
 * completed task with a video link) from the current active sprint.
 *
 * It's efficient because it reuses the data from `useSprintsList` and `useSprintBoard`,
 * benefiting from SWR's caching layer.
 */
export const useLatestSprintUpdate = () => {
  const { i18n } = useTranslation();

  const isVideoLink = (link: TaskLink): link is TaskLink & { url: string } =>
    typeof link.url === 'string' &&
    (link.url.includes('youtube.com') || link.url.includes('vimeo.com'));
  
  // 1. Get the list of all sprints to find the active one.
  const { sprints, isLoading: isLoadingSprints } = useSprintsList();

  // Find the active sprint. Fallback to the first sprint if none are 'Active'.
  const activeSprint = useMemo(() => {
    if (!sprints) return null;
    return sprints.find(s => s.status === 'Active') || sprints[0] || null;
  }, [sprints]);

  // 2. Fetch the detailed dashboard data for only the active sprint.
  // SWR ensures this only runs if `activeSprint.id` is valid.
  const { sprintDashboardData, isLoading: isLoadingBoard } = useSprintBoard(
    activeSprint?.id, 
    i18n.language
  );

  // 3. Perform the search logic using a memoized calculation.
  const latestUpdate = useMemo<LatestUpdateData | null>(() => {
    // Guard against missing data.
    if (!sprintDashboardData?.board) {
      return null;
    }

    const { board } = sprintDashboardData;
    const { sprint, days, tasks } = board;

    // Sort days from most recent to oldest to find the latest update first.
    const sortedDays = [...days].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    // Iterate through each day, starting from the most recent.
    for (const day of sortedDays) {
      // Find tasks that are part of the current day's plan.
      const tasksInDay = tasks.filter(task => {
        const taskId = task._id ?? task.id;
        return typeof taskId === 'string' && day.task_ids.includes(taskId);
      });
      
      // Filter for tasks that represent a tangible "update":
      // They must be done and have at least one media link.
      const relevantTasks = tasksInDay.filter(task => {
        const isCompleted = task.status === 'done' || task.status === 'completed';
        return isCompleted && (task.links?.length ?? 0) > 0;
      });

      if (relevantTasks.length > 0) {
        // Get the last relevant task of the day.
        const latestTask = relevantTasks[relevantTasks.length - 1];
        
        // Find the first link that is a video.
        const videoLink = (latestTask.links ?? []).find(isVideoLink);

        if (videoLink) {
          // We've found our latest update. Return it and stop the search.
          return {
            titleKey: latestTask.title, // The API provides a translatable key here
            url: videoLink.url,
            posterUrl: undefined, // Can be extended in the future
            dayNumber: day.day_number,
            sprintId: sprint.id,
          };
        }
      }
    }
    // If the loop completes without finding a suitable video update, return null.
    return null;
  }, [sprintDashboardData]); // This logic only re-runs when the dashboard data changes.

  return {
    /** The found update object, or null if no update is available. */
    latestUpdate,
    /** A loading state that is true if either the sprint list or the active sprint's board is loading. */
    isLoading: isLoadingSprints || isLoadingBoard,
  };
};
